import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FilterPanel from '../../components/ui/FilterPanel';
import { useHubupCommunities } from '../../context/HubupCommunitiesContext';
import { useUserAccess } from '../../context/UserAccessContext';
import HubupQuotaExhaustedBanner from '../../components/hubup/HubupQuotaExhaustedBanner';
import HubupNotificationOffersModal, {
  confirmHubupPackReturnFromUrl,
} from '../../components/hubup/HubupNotificationOffersModal';
import Icon from '../../components/AppIcon';
import { subscribeHubupGenerateJob } from '../../lib/hubupGenerateNotificationJob';

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground text-sm">
    Chargement...
  </div>
);

export default function HubupGroupLayout() {
  const { groupId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { communities, loading, refetch } = useHubupCommunities();
  const { user, profile, chooseAppMode, refreshUser } = useUserAccess();
  const [offersOpen, setOffersOpen] = useState(false);
  const [offersSuccess, setOffersSuccess] = useState(false);
  const [packConfirmError, setPackConfirmError] = useState(null);
  const [generateJob, setGenerateJob] = useState({ inFlight: false, groupId: null });
  const isAdmin = user?.permission === 'admin';
  const currentCommunity = communities.find((c) => c.id === groupId);
  const notifRemaining =
    !isAdmin && currentCommunity
      ? Math.max(0, Number(currentCommunity.notificationLeft) || 0)
      : null;
  const quotaExhausted = notifRemaining !== null && notifRemaining <= 0;

  useEffect(() => {
    if (profile?.kind === 'dual' && location.pathname.startsWith('/hubup')) {
      chooseAppMode('hubup');
    }
  }, [profile?.kind, location.pathname, chooseAppMode]);

  useEffect(() => {
    if (!groupId || searchParams.get('hubupPack') !== 'return') return;
    let cancelled = false;
    (async () => {
      const result = await confirmHubupPackReturnFromUrl(groupId, searchParams);
      if (cancelled) return;
      navigate(location.pathname, { replace: true });
      if (result.ok) {
        setPackConfirmError(null);
        setOffersSuccess(true);
        setOffersOpen(true);
        await refetch();
        await refreshUser?.();
      } else if (result.reason === 'error') {
        setPackConfirmError(result.message);
        setOffersOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, searchParams, navigate, location.pathname, refetch, refreshUser]);

  useEffect(() => {
    const openOffers = () => setOffersOpen(true);
    window.addEventListener('hubup:open-offers', openOffers);
    return () => window.removeEventListener('hubup:open-offers', openOffers);
  }, []);

  useEffect(() => {
    return subscribeHubupGenerateJob(setGenerateJob);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!communities.length) {
    return <Navigate to="/hubup" replace />;
  }

  const valid = communities.some((c) => c.id === groupId);
  if (!valid) {
    return (
      <Navigate
        to={`/hubup/g/${communities[0].id}/dashboard`}
        replace
      />
    );
  }

  const canBuyPacks = !isAdmin && user?.hubup === true;
  const onNotificationsPage =
    groupId &&
    (location.pathname === `/hubup/g/${groupId}/notifications` ||
      location.pathname.endsWith('/notifications'));
  const showGenerateJobBanner =
    generateJob.inFlight &&
    generateJob.groupId === groupId &&
    !onNotificationsPage;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <FilterPanel />
        {quotaExhausted ? (
          <HubupQuotaExhaustedBanner
            onViewOffers={canBuyPacks ? () => setOffersOpen(true) : undefined}
          />
        ) : null}
        {showGenerateJobBanner ? (
          <div
            className="mx-4 md:mx-6 lg:mx-8 mt-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3"
            role="status"
          >
            <Icon
              name="Loader2"
              size={20}
              className="text-primary animate-spin shrink-0 mt-0.5"
            />
            <div className="text-sm space-y-1">
              <p className="font-medium text-foreground">Préparation de notification…</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Vous pouvez continuer à naviguer. Le brouillon sera enregistré automatiquement
                dans « Mes notifications ».
              </p>
            </div>
          </div>
        ) : null}
        {packConfirmError && !offersOpen ? (
          <p className="mx-4 md:mx-6 lg:mx-8 mt-2 text-sm text-error" role="alert">
            {packConfirmError}
          </p>
        ) : null}
        <Outlet />
      </div>
      <HubupNotificationOffersModal
        open={offersOpen}
        onClose={() => {
          setOffersOpen(false);
          setOffersSuccess(false);
          setPackConfirmError(null);
        }}
        groupId={groupId}
        communityName={currentCommunity?.name}
        initialSuccess={offersSuccess}
        initialError={packConfirmError}
        onPurchaseComplete={() => {
          void refetch();
          void refreshUser?.();
        }}
      />
    </div>
  );
}
